import React, { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useOrganization } from '@/contexts/organization-context'
import { useAuth } from '@/contexts/auth-context'
import { api } from '@/services/api'
import { BILLING_BLOCKED_EVENT, billingBlockMessage } from '@/utils/billing-block'

interface SubscriptionGuardProps {
  children: React.ReactNode
}

export function SubscriptionGuard({ children }: SubscriptionGuardProps) {
  const location = useLocation()
  const { currentOrganization } = useOrganization()
  const { user } = useAuth()
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null)
  // One-way flag: false until the FIRST probe resolves, then stays true.
  // Previously this was a `loading` state that was flipped back to true on
  // every re-check (and the effect re-ran on every navigation), so each route
  // change unmounted the whole DashboardLayout and remounted it, re-firing all
  // shell + page queries. Gating only on the first check keeps the shell
  // mounted; later re-probes run in the background.
  const [initialCheckDone, setInitialCheckDone] = useState(false)

  // Routes that must render even for a blocked org (auth flows). Everything else is
  // replaced by the upgrade screen: the api 403s every gated endpoint, so rendering
  // the app just produces a broken page and a wall of identical console errors.
  const exemptPages = ['/login', '/signup', '/oauth-callback']

  // There is NO client-side admin bypass. The api gates per-organization and is the
  // source of truth; an exempt org simply reports the subscription as valid.
  useEffect(() => {
    let cancelled = false

    const check = async () => {
      if (!currentOrganization || !user) return
      if (exemptPages.some((p) => location.pathname.includes(p))) {
        setInitialCheckDone(true)
        return
      }
      try {
        // A cheap gated endpoint; a billing block comes back as a recognisable 403.
        await api.get('/normalizations/?page=1&size=1')
        if (!cancelled) setBlockedMessage(null)
      } catch (error: any) {
        // Any OTHER error (network, permissions) must not lock the user out.
        const message = billingBlockMessage(error)
        if (!cancelled) setBlockedMessage(message)
      } finally {
        // Never reverts to false — re-checks on later navigations happen in the
        // background without unmounting the shell.
        if (!cancelled) setInitialCheckDone(true)
      }
    }

    check()
    return () => {
      cancelled = true
    }
  }, [currentOrganization, user, location.pathname])

  // Any gated request anywhere in the app may reveal the block before our probe does.
  useEffect(() => {
    const onBlocked = (e: Event) => {
      const detail = (e as CustomEvent).detail
      setBlockedMessage(detail?.message ?? 'Upgrade required to continue.')
      setInitialCheckDone(true)
    }
    window.addEventListener(BILLING_BLOCKED_EVENT, onBlocked)
    return () => window.removeEventListener(BILLING_BLOCKED_EVENT, onBlocked)
  }, [])

  if (!initialCheckDone) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  // Send them to the standalone upgrade page rather than rendering the app shell.
  // Children (DashboardLayout, sidebar, page widgets) each call a gated endpoint, so
  // rendering them for a blocked org yields a broken page and a wall of 403s.
  if (blockedMessage && currentOrganization) {
    return <Navigate to={`/${currentOrganization.slug}/upgrade`} replace />
  }

  return <>{children}</>
}
